import { useEffect, useEffectEvent } from 'react';
import type { Command } from '@/game/sim';
import { unfocusLastCommand } from '@/game/view/focusQueue';
import type { FrameStore } from '@/game/view/frame';
import { groupCommand, ungroupCommand } from '@/game/view/groups';
import { renameTarget } from '@/game/view/names';
import { slotForSelection, slotNameKey } from '@/game/view/slots';
import { claimsAKey, toggleHoldCommand } from '@/game/view/stance';
import { toggleEmpire } from '../hud/EmpireDrawer';
import { startRename, type UiStore } from '../ui/store';

/**
 * Keyboard: Escape closes the settings panel and key legend, else cancels placement or targeting, else closes the Upgrades dock, else goes back to the plain map in one press (cancels
 * placement and targeting, closes the city or building panel and the Send
 * troops picker, clears the unit selection: `onEscape` closes the rest); G
 * groups the selection (a formation), U ungroups it, F2 renames the selected
 * unit, formation, control group or city (inline, `InlineRename`), Ctrl/Cmd+
 * 1–9 store the selection in control slot N, and 1–9 recall slot N, or
 * formation Group N while the slot is empty (pressed again while it's the
 * selection, go to it; shift adds), Y toggles the Upgrades dock, E the
 * Empire drawer, ? the key legend, Backspace
 * drops the last queued focus target, H toggles hold for its fighters, A
 * starts an attack-move; . jumps to the next idle worker. The group keys are
 * off while placing or targeting, with other modifiers held, or while typing.
 * The settings panel lists every binding (view/hotkeys.ts): keep it in step.
 */
export function useHotkeys({
  ui,
  frames,
  dispatch,
  onEscape,
  onDigit,
  onAssignSlot,
  onIdleCycle,
  onAttackMove,
}: {
  ui: UiStore;
  frames: FrameStore;
  dispatch: (command: Command) => void;
  onEscape: () => void;
  /** A digit key: control slot N, else formation Group N (shift adds). */
  onDigit: (number: number, additive: boolean) => void;
  /** Ctrl/Cmd+digit: the selection becomes slot N. */
  onAssignSlot: (number: number) => void;
  onIdleCycle: () => void;
  /** Enter the attack-move targeting mode. */
  onAttackMove: () => void;
}) {
  const onKeyDown = useEffectEvent((e: KeyboardEvent) => {
    // The rename input handles its own Esc (cancel); it must not clear the selection.
    if (
      e.key === 'Escape' &&
      e.target instanceof HTMLElement &&
      e.target.closest('[data-rename]')
    ) {
      return;
    }
    if (e.key === 'Escape' && (ui.get().settingsOpen || ui.get().legendOpen)) {
      ui.set({ settingsOpen: false, legendOpen: false });
      return;
    }
    if (e.key === 'Escape') {
      // Placement or targeting cancels first, then the Upgrades dock; the next Esc clears the rest.
      const { upgradesOpen, placementMode, targetMode } = ui.get();
      if (upgradesOpen && !placementMode && !targetMode) {
        ui.set({ upgradesOpen: false });
        return;
      }
      ui.set({
        placementMode: null,
        targetMode: null,
        selectedUnitIds: [],
        selectedBuildingId: null,
      });
      onEscape();
      return;
    }
    const { placementMode, targetMode, selectedUnitIds } = ui.get();
    // By code, so shift (add to the selection) still reads as a digit.
    const digit = /^Digit([1-9])$/.exec(e.code);
    const assign = !!digit && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey;
    if (((e.ctrlKey || e.metaKey || e.altKey) && !assign) || placementMode || targetMode) return;
    if (e.target instanceof HTMLElement && e.target.closest('input, textarea, select')) return;
    if (assign) {
      // Where the browser allows it, keep Ctrl+N from switching tabs.
      e.preventDefault();
      if (!e.repeat) onAssignSlot(Number(digit[1]));
      return;
    }
    const { view, playerId, rules } = frames.get();
    if (e.key === 'F2') {
      // The bar shows a selected slot as selected: it's the slot that gets renamed.
      const slot = slotForSelection(view, playerId, ui.get().slots, selectedUnitIds);
      if (slot !== null) {
        e.preventDefault();
        startRename(ui, slotNameKey(slot), 'slot');
        return;
      }
      const target = renameTarget(
        view,
        rules,
        playerId,
        selectedUnitIds,
        ui.get().selectedBuildingId
      );
      if (target) {
        e.preventDefault();
        startRename(ui, target.id, target.site);
      }
      return;
    }
    const key = e.key.toLowerCase();
    if (key === 'g') {
      const command = groupCommand(view, playerId, selectedUnitIds);
      if (command) dispatch(command);
    } else if (key === 'h' && !e.repeat) {
      const command = toggleHoldCommand(view, rules, playerId, selectedUnitIds);
      if (command) dispatch(command);
    } else if (key === 'a' && !e.repeat) {
      if (claimsAKey(view, rules, playerId, selectedUnitIds, false)) onAttackMove();
    } else if (e.key === 'Backspace' && !e.repeat) {
      const command = unfocusLastCommand(view, selectedUnitIds, playerId);
      if (command) {
        e.preventDefault();
        dispatch(command);
      }
    } else if (key === 'u') {
      const command = ungroupCommand(view, playerId, selectedUnitIds);
      if (command) dispatch(command);
    } else if (key === 'y') {
      ui.set((s) => ({ upgradesOpen: !s.upgradesOpen }));
    } else if (key === 'e' && !e.repeat) {
      ui.set(toggleEmpire);
    } else if (e.key === '?' && !e.repeat) {
      ui.set((s) => ({ legendOpen: !s.legendOpen }));
    } else if ((key === '.' || e.code === 'Period') && !e.repeat) {
      onIdleCycle();
    } else if (digit && !e.repeat) {
      onDigit(Number(digit[1]), e.shiftKey);
    }
  });
  useEffect(() => {
    const listener = (e: KeyboardEvent) => onKeyDown(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);
}
